// 013B — Anatomía Patológica Informe (Anexo 1 MSP, SNS-MSP/HCU-form.013B/2021). Fase 7, Tanda 4: datos del servicio,
// macroscopía y microscopía (cada una con su profesional responsable), informe de citología vaginal (Bethesda),
// diagnóstico y recomendaciones. Campos y decisiones en `bloquesAnatomiaPatologicaDatos.ts`. H (profesional responsable) no se teclea: lo arma el servidor al firmar. El papel no trae
// "N° de pieza" ni "N° de informe" como tales (el código interno de muestra los reemplaza).
import { useMedplum } from '@medplum/react-hooks';
import type { ServiceRequest } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type { FormularioDiscretoProps } from './index';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { AreaDictado } from './AreaDictado';
import { CATEGORIA_ANATOMIA_PATOLOGICA } from './categoriasDiagnosticas';
import { Bloque, CamposSimples } from './camposSimples';
import { conCodigoCie, type CampoSimple, type Valores } from './camposSimplesDatos';
import { SelectorRecurso } from './SelectorRecurso';
import { codeDeDiagnostico, SelectorDiagnosticoCie10, type DiagnosticoCie10Valor } from './SelectorDiagnosticoCie10';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import {
  CAMPOS_CITOLOGIA_013B,
  CAMPOS_DIAGNOSTICO_013B,
  CAMPOS_MACROSCOPICA_013B,
  CAMPOS_MICROSCOPICA_013B,
  CAMPOS_RECOMENDACIONES_013B,
  CAMPOS_SERVICIO_013B,
  conclusionInforme013B,
  estadoInicial013B,
  paresDe013B,
  type Estado013B,
} from './bloquesAnatomiaPatologicaDatos';

const sinAreas = (campos: readonly CampoSimple[]): readonly CampoSimple[] => campos.filter((c) => c.tipo !== 'area');
const areaDe = (campos: readonly CampoSimple[]): CampoSimple => campos.find((c) => c.tipo === 'area')!;

const DIAGNOSTICO_VACIO: DiagnosticoCie10Valor = { texto: '', cie10: null };

export function Formulario013B({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const [solicitud, setSolicitud] = useState<ServiceRequest>();
  const [estado, setEstado] = useState<Estado013B>(() => estadoInicial013B());
  const [cie, setCie] = useState<DiagnosticoCie10Valor>(DIAGNOSTICO_VACIO);
  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const { guardando, error, guardar } = useGuardado();
  // Servicio del encabezado del PDF (no el 'Consulta externa' por defecto): Informe del laboratorio de anatomía patológica: el papel no trae servicio (su bloque B solo pide establecimiento/profesional/especialidad).
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, '013B', t('formularios.f013b.titulo'), { servicio: 'Anatomía patológica' });

  const cambiar = <K extends keyof Estado013B>(clave: K, valores: Valores): void => setEstado({ ...estado, [clave]: valores });
  const bloqueArea = (clave: 'macroscopica' | 'microscopica' | 'citologia' | 'diagnostico' | 'recomendaciones', campo: CampoSimple, id: string, filas: number): JSX.Element => (
    <AreaDictado id={id} etiqueta={campo.etiqueta} valor={estado[clave][campo.clave] ?? ''} filas={filas} onChange={(v) => cambiar(clave, { ...estado[clave], [campo.clave]: v })} />
  );

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      const ahora = new Date().toISOString();
      await medplum.createResource({
        resourceType: 'DiagnosticReport',
        status: 'final',
        category: [CATEGORIA_ANATOMIA_PATOLOGICA],
        code: solicitud?.code ?? { text: estado.servicio.codigoInterno?.trim() || CATEGORIA_ANATOMIA_PATOLOGICA.text },
        subject,
        encounter,
        basedOn: solicitud?.id ? [{ reference: `ServiceRequest/${solicitud.id}` }] : undefined,
        effectiveDateTime: ahora,
        issued: ahora,
        conclusion: conclusionInforme013B(estado),
        conclusionCode: cie.texto.trim() ? [codeDeDiagnostico(cie)] : undefined,
      });
      const pares = [
        ...(solicitud ? [{ pregunta: t('formularios.f013b.solicitudOrigen'), respuesta: solicitud.code?.text ?? solicitud.id ?? '' }] : []),
        ...paresDe013B(estado),
        ...(cie.texto.trim() ? [{ pregunta: t('formularios.f013b.codigoCie'), respuesta: conCodigoCie(cie) }] : []),
      ];
      onGuardado(pares, datosEncabezadoMsp);
    });

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{t('formularios.f013b.titulo')}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <SelectorRecurso<'ServiceRequest'>
        resourceType="ServiceRequest"
        subject={subject}
        label={t('formularios.f013b.solicitudOrigen')}
        etiqueta={(r) => r.code?.text ?? r.id ?? ''}
        value={solicitud}
        onChange={setSolicitud}
      />

      <Bloque titulo={t('formularios.f013b.servicioTitulo')}>
        <CamposSimples prefijoId="f013b-srv" campos={CAMPOS_SERVICIO_013B} valores={estado.servicio} columnas={2} onChange={(v) => cambiar('servicio', v)} />
      </Bloque>

      <Bloque titulo={t('formularios.f013b.macroscopicaTitulo')}>
        {bloqueArea('macroscopica', areaDe(CAMPOS_MACROSCOPICA_013B), 'f013b-macroscopica', 6)}
        <CamposSimples prefijoId="f013b-mac" campos={sinAreas(CAMPOS_MACROSCOPICA_013B)} valores={estado.macroscopica} columnas={2} onChange={(v) => cambiar('macroscopica', { ...estado.macroscopica, ...v })} />
      </Bloque>

      <Bloque titulo={t('formularios.f013b.microscopicaTitulo')}>
        {bloqueArea('microscopica', areaDe(CAMPOS_MICROSCOPICA_013B), 'f013b-microscopica', 10)}
        <CamposSimples prefijoId="f013b-mic" campos={sinAreas(CAMPOS_MICROSCOPICA_013B)} valores={estado.microscopica} columnas={2} onChange={(v) => cambiar('microscopica', { ...estado.microscopica, ...v })} />
      </Bloque>

      <Bloque titulo={t('formularios.f013b.citologiaTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f013b.citologiaNota')}</p>
        <CamposSimples prefijoId="f013b-cit" campos={sinAreas(CAMPOS_CITOLOGIA_013B)} valores={estado.citologia} columnas={3} onChange={(v) => cambiar('citologia', { ...estado.citologia, ...v })} />
        {bloqueArea('citologia', areaDe(CAMPOS_CITOLOGIA_013B), 'f013b-cit-observaciones', 3)}
      </Bloque>

      <Bloque titulo={t('formularios.f013b.diagnosticoTitulo')}>
        {bloqueArea('diagnostico', areaDe(CAMPOS_DIAGNOSTICO_013B), 'f013b-diagnostico', 4)}
        <SelectorDiagnosticoCie10 label={t('formularios.f013b.codigoCie')} value={cie} onChange={setCie} />
      </Bloque>

      <Bloque titulo={t('formularios.f013b.recomendacionesTitulo')}>
        {bloqueArea('recomendaciones', areaDe(CAMPOS_RECOMENDACIONES_013B), 'f013b-recomendaciones', 3)}
      </Bloque>

      <Bloque titulo={t('formularios.f013b.profesionalTitulo')}>
        <p className="text-sm text-hce-muted">{t('formularios.profesionalNotaCedula')}</p>
      </Bloque>

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
